import { useState } from "react";
import { Pressable, TextInput } from "react-native";
import { getTokenValue, useTheme } from "tamagui";

import { AssistantClientEvent } from "@/api/model/Conversation";
import { useSendNoriMessage } from "@/api/mutations/conversation";
import { Button, Text, XStack, YStack } from "@/components";
import { useCurrentConversationId } from "@/hooks/conversation";

type CommentableContentProps = Omit<AssistantClientEvent, "content"> & {
  content: {
    title: string;
    fields?: { label: string; value: string }[];
    body: string[];
  };
  isLatest: boolean;
};

const CommentableContent = ({ content, isLatest }: CommentableContentProps) => {
  const theme = useTheme();
  const conversationId = useCurrentConversationId();
  const { sendNoriMessage } = useSendNoriMessage(conversationId);
  const [comments, setComments] = useState<Record<number, string>>({});
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const disabled = !isLatest || submitted;
  const hasComments = Object.values(comments).some((c) => c.trim());

  const handleLooksGood = () => {
    setSubmitted(true);
    sendNoriMessage("Looks good, please generate the preview.");
  };

  const handleRequestChanges = () => {
    const lines = Object.entries(comments)
      .filter(([, c]) => c.trim())
      .map(([idx, c]) => {
        const para = content.body[Number(idx)];
        const excerpt = para.length > 60 ? para.slice(0, 60) + "..." : para;
        return `- On the paragraph starting with "${excerpt}": ${c.trim()}`;
      });
    if (lines.length === 0) return;
    setSubmitted(true);
    sendNoriMessage(
      `Please revise the document with these changes:\n${lines.join("\n")}`,
    );
  };

  return (
    <YStack gap="$300">
      <YStack gap="$100">
        <Text semibold>{content.title}</Text>
        {(content.fields ?? []).map((f) => (
          <Text key={f.label} color="$textSecondary">
            {f.label}: {f.value}
          </Text>
        ))}
      </YStack>

      {(content.body ?? []).map((paragraph, i) =>
        !paragraph.trim() ? null : (
          <YStack key={i} gap="$100">
            <Text>{paragraph}</Text>
            {comments[i] && activeIdx !== i && (
              <XStack gap="$100" alignItems="flex-start">
                <Text fontSize="$2" color="$textSecondary" flex={1}>
                  Note: {comments[i]}
                </Text>
                {!disabled && (
                  <Pressable onPress={() => setActiveIdx(i)}>
                    <Text fontSize="$sm" color="$textTertiary">
                      Edit
                    </Text>
                  </Pressable>
                )}
              </XStack>
            )}
            {activeIdx === i && (
              <TextInput
                autoFocus
                multiline
                value={comments[i] ?? ""}
                onChangeText={(text) =>
                  setComments((prev) => ({ ...prev, [i]: text }))
                }
                onBlur={() => setActiveIdx(null)}
                placeholder="Describe what to change..."
                style={{
                  borderWidth: getTokenValue("$strokeWidth", "size"),
                  borderColor: theme.strokeNeutral.get(),
                  borderRadius: 6,
                  padding: 8,
                  fontSize: 13,
                  minHeight: 56,
                }}
              />
            )}
            {!disabled &&
              !comments[i] &&
              activeIdx !== i &&
              paragraph.trim() && (
                <Pressable onPress={() => setActiveIdx(i)}>
                  <Text fontSize="$sm" color="$textTertiary">
                    + Add comment
                  </Text>
                </Pressable>
              )}
          </YStack>
        ),
      )}

      {!disabled && (
        <YStack gap="$100" marginBottom="$100">
          {hasComments && (
            <Button mode="secondary" onPress={handleRequestChanges}>
              Request Changes
            </Button>
          )}
          <Button onPress={handleLooksGood}>Looks Good</Button>
        </YStack>
      )}
    </YStack>
  );
};

export default CommentableContent;
